import { tagName } from "@ember-decorators/component"
import { getOwner } from "@ember/application"
import Component from "@ember/component"
import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import * as Sentry from "@sentry/browser"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import importTransferrableArrayAction from "cc-frontend/lib/actions/course/calendar/IMPORT_TRANSFERRABLE_ARRAY"
import PLANBOOK_SET_FLAG from "cc-frontend/lib/actions/planbook/PLANBOOK_SET_FLAG"
import type Router from "cc-frontend/router"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type Rpc from "cc-frontend/services/rpc"
import { easeIn, easeOut } from "ember-animated/easings/cosine"
import move from "ember-animated/motions/move"
import { fadeIn, fadeOut } from "ember-animated/motions/opacity"
import _, { find, forEach } from "lodash"
import styles from "./modal.module.css"

const forwardTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: -window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}
const backwardsTransition = function* ({ insertedSprites, keptSprites, removedSprites }) {
  insertedSprites.forEach((sprite) => {
    sprite.startAtPixel({ x: -window.innerWidth })
    fadeIn(sprite, { easing: easeOut, duration: 500 })
    // move(sprite, { easing: easeOut, duration: 500 })
  })

  keptSprites.forEach(move)

  removedSprites.forEach((sprite) => {
    sprite.endAtPixel({ x: window.innerWidth })
    // move(sprite, { easing: easeIn, duration: 500 })
    fadeOut(sprite, { easing: easeIn, duration: 500 })
  })
}

@tagName("")
export default class EditClassMainModal extends Component {
  readonly styles = styles

  planbook!: Planbook
  rotationCalendar!: RotationCalendar
  onClose!: Function
  courses!: Array<Course>

  @service rpc!: Rpc
  @service dispatcher!: Dispatcher
  @service notifier

  // state
  currentScreen = "INTRO"
  newDateString
  importDateString: string = dateFns.format(new Date(), "YYYY-MM-DD")
  weekNumber
  weekday
  weekdayNumber
  monthNumber
  datePhrase
  shouldPreserveDates: boolean = false
  usedDatePicker: boolean = false
  center: Date = new Date()

  animationRules({ oldItems, newItems }) {
    let newVal = newItems[0]
    let oldVal = oldItems[0]
    if (oldVal < newVal) {
      // return toLeft
      // return toUp
      return forwardTransition
    } else {
      // return toRight
      // return toDown
      return backwardsTransition
    }
  }

  @computed("importDateString")
  get selectedDateAsDate() {
    return this.importDateString ? dateFns.parse(this.importDateString) : new Date()
  }

  @action
  goToAdjustSchedule() {
    this.set("currentScreen", "ADJUST_SCHEDULE")
  }

  @action
  goToNextAfterAdjustSchedule() {
    // If copying within the same school year, show the choice screen
    if (this.planbook.attributes.flags.isSameSchoolYearAsCopy) {
      this.set("currentScreen", "CHOOSE_DATE_STRATEGY")
    } else {
      // Different school year - go straight to date selection
      this.goToConfirmDate()
    }
  }

  @action
  chooseKeepSameDates() {
    this.set("shouldPreserveDates", true)
    this.set("currentScreen", "CONFIRMATION")
  }

  @action
  chooseShiftDates() {
    this.set("shouldPreserveDates", false)
    this.set("usedDatePicker", true)
    // Skip CONFIRM_DATE and go straight to PICK_DATE since user wants to choose their own date
    this.set("currentScreen", "PICK_DATE")
  }

  @action
  goToConfirmDate() {
    this.rpc
      .execute("planbookcopier_get_start_dates_from_parent_courses", {
        planbookId: this.planbook.id,
        courseIds: _.map(this.planbook.relationships.courses.data, (datum) => datum.id),
      })
      .then((response) => {
        let { newDateString, weekNumber, weekdayNumber, monthNumber, datePhrase } = response
        this.setProperties({
          newDateString,
          weekNumber,
          weekdayNumber,
          monthNumber,
          datePhrase,
        })
        this.set("currentScreen", "CONFIRM_DATE")
      })
  }

  @action
  goBackFromConfirmDate() {
    if (this.planbook.attributes.flags.isSameSchoolYearAsCopy) {
      this.set("currentScreen", "CHOOSE_DATE_STRATEGY")
    } else {
      this.set("currentScreen", "ADJUST_SCHEDULE")
    }
  }

  @action
  goBackFromPickDate() {
    // If same school year, go back to the choice screen; otherwise go to CONFIRM_DATE
    if (this.planbook.attributes.flags.isSameSchoolYearAsCopy) {
      this.set("currentScreen", "CHOOSE_DATE_STRATEGY")
    } else {
      this.set("currentScreen", "CONFIRM_DATE")
    }
  }

  @action
  goBackFromConfirmation() {
    if (this.shouldPreserveDates) {
      // They chose to keep same dates, go back to the choice screen
      if (this.planbook.attributes.flags.isSameSchoolYearAsCopy) {
        this.set("currentScreen", "CHOOSE_DATE_STRATEGY")
      } else {
        this.set("currentScreen", "ADJUST_SCHEDULE")
      }
    } else if (this.usedDatePicker) {
      // They went through the date picker, go back to PICK_DATE
      this.set("currentScreen", "PICK_DATE")
    } else {
      // They chose the default date on CONFIRM_DATE screen, go back there
      this.set("currentScreen", "CONFIRM_DATE")
    }
  }

  @action
  confirmDefaultDate() {
    this.set("importDateString", this.newDateString)
    this.set("usedDatePicker", false)
    this.set("currentScreen", "CONFIRMATION")
  }

  @action
  goToPickDate() {
    this.set("usedDatePicker", true)
    this.set("currentScreen", "PICK_DATE")
  }

  @action
  goToConfirmation() {
    this.set("currentScreen", "CONFIRMATION")
  }

  @action
  selectDate(date) {
    this.set("importDateString", dateFns.format(date, "YYYY-MM-DD"))
  }

  @action
  copyLessons() {
    this.set("currentScreen", "IS_COPYING_LESSONS")
    this.rpc
      .execute("planbookcopier_import_lessons_from_parent_courses", {
        planbookId: this.planbook.id,
        courseIds: _.map(this.planbook.relationships.courses.data, (datum) => datum.id),
        importDateString: this.importDateString || undefined,
        preserveDates: this.shouldPreserveDates,
      })
      .then((ret) => {
        if (ret.ok === false) {
          this.set("currentScreen", "CONFIRMATION")
          Sentry.withScope((scope) => {
            scope.setLevel(Sentry.Severity.Error)
            scope.setExtra("planbookId", this.planbook.id)
            scope.setExtra("error", ret.error)
            scope.setExtra(
              "courseIds",
              _.map(this.planbook.relationships.courses.data, (datum) => datum.id)
            )
            scope.setExtra("importDateStrings", this.importDateString)
            Sentry.captureMessage("Import Planbook Failed")
          })
          this.notifier.warning("", {
            notificationId: this.planbook.id,
            title: `Error copying lessons`,
            message: "Try again shortly.",
            duration: 5000,
            type: "is-processing",
          })
        } else {
          forEach(this.courses, (course) => {
            let result = find(ret.data, (datum) => datum.courseToImportIntoId === course.id)
            let transferrableArray = result.transferrableArray
            let newStartDate = dateFns.parse(result.newStartDate)
            this.dispatcher.dispatchPreparedAction(
              importTransferrableArrayAction({
                transferrableArray: transferrableArray,
                course: course,
                planbook: this.planbook,
                rotationCalendar: this.rotationCalendar,
                newStartDate: newStartDate,
                originalCourseId: course.attributes.parentCourse.id,
                originalPlanbookId: course.attributes.parentCourse.planbookId,
              })
            )
            this.dispatcher.dispatchAction(PLANBOOK_SET_FLAG, {
              planbookId: this.planbook.id,
              field: "hasNotFinishedOnboarding",
              value: false,
            })
            this.dispatcher.dispatchAction(PLANBOOK_SET_FLAG, {
              planbookId: this.planbook.id,
              field: "isImportingLessons",
              value: false,
            })
          })
          this.set("currentScreen", "ALL_DONE")
        }
      })
  }

  @action
  goToWeek() {
    ;(getOwner(this)!.lookup("router:main") as Router).transitionTo(
      "planbookv5.planner.week",
      this.planbook.id,
      {
        queryParams: { date: this.importDateString },
      }
    )
  }
}
